import { useState, useMemo } from 'react';
import { Box, Flex, Icon, IconButton, Text } from '@chakra-ui/react';
import api from '@/utils/api';
import { toaster } from '@/components/ui/toaster';
import { Tooltip } from '@/components/ui/tooltip';
import { SortableSimpleList } from '@/components/sortable-simple-list';
import SortableItem from '@/components/sortable-simple-item';
import useModalToggle from '@/lib/hooks/use-modal-toggle';
import ContentDrawer from '@/pages/contents/content-drawer';
import { Content, GroupForm } from '@/lib/types';
import { EyeIcon, TrashIcon } from '@heroicons/react/24/outline';

interface Props {
  groupItem: GroupForm;
  groupIndex: number;
  contents: Content[];
  onChange(row: GroupForm, index: number): void;
  removeItem(groupIndex: number, referencId: string): void;
}

export default function ContentList({
  groupItem,
  groupIndex,
  contents,
  onChange,
  removeItem,
}: Props) {
  const supportDrawerModal = useModalToggle();
  const [selectedContent, setSelectedContent] = useState<any>();

  const contentsMap = useMemo(() => {
    const map = new Map<string, Content>();
    contents.forEach((item) => {
      map.set(item.reference_id, item);
    });
    return map;
  }, [contents]);

  function handleSortEnd(refs: string[]) {
    onChange({ ...groupItem, contents: refs }, groupIndex);
  }

  async function handleView(item: string) {
    try {
      const response = await api.get(`public/content/${item}`);
      const content = await response.data.data;
      if (!content) {
        return;
      }
      setSelectedContent(content);
      supportDrawerModal.open();
    } catch (err: any) {
      toaster.create({
        type: 'error',
        title: err.response?.data?.error || 'Failed to fetch content',
      });
    }
  }

  const renderItem = (item: string, isOverlay = false) => {
    const content = (
      <Flex alignItems={'center'} width={'100%'} justifyContent={'space-between'}>
        <Box>
          <Text fontSize={'sm'} fontWeight={'medium'} lineHeight={1}>
            {contentsMap.get(item)?.name}
          </Text>
          <Text fontSize={'label'} fontStyle={'italic'} lineHeight={1}>
            {contentsMap.get(item)?.content_type_name}
          </Text>
        </Box>
        <Box>
          <Tooltip content={'View content'} aria-label={'View content'}>
            <IconButton
              size={'sm'}
              variant={'plain'}
              aria-label={'View content'}
              onClick={() => handleView(item)}
            >
              <Icon as={EyeIcon} h={4} w={4} />
            </IconButton>
          </Tooltip>
          <Tooltip content={'Remove content'} aria-label={'Remove content'}>
            <IconButton
              size={'sm'}
              variant={'plain'}
              aria-label={'Remove content'}
              onClick={() => removeItem(groupIndex, item)}
            >
              <Icon as={TrashIcon} h={4} w={4} />
            </IconButton>
          </Tooltip>
        </Box>
      </Flex>
    );

    return <SortableItem key={item} id={item} content={content} isOverlay={isOverlay} />;
  };

  return (
    <>
      <SortableSimpleList
        items={groupItem.contents || []}
        onSortEnd={handleSortEnd}
        renderItem={renderItem}
      />

      {selectedContent && <ContentDrawer {...supportDrawerModal.props} content={selectedContent} />}
    </>
  );
}
